import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, MessageCircle, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { API, IS_STATIC, waLink, WHATSAPP_DISPLAY, CONTACTS } from "../constants/site";
import { ChapterHeader } from "./ChapterHeader";

const INPUT_CLS =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-5 py-4 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-sunset";

export const EnquiryForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", destination: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (IS_STATIC) {
      const text = `New Enquiry — Gautam Travel\nName: ${form.name}\nPhone: ${form.phone}\nDestination: ${form.destination}\nDetails: ${form.message}`;
      window.open(waLink(text), "_blank", "noreferrer");
      toast.success("Opening WhatsApp — hit send and we'll get back to you!");
      setForm({ name: "", phone: "", destination: "", message: "" });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent! We'll reach out to you shortly.");
      setForm({ name: "", phone: "", destination: "", message: "" });
    } catch {
      toast.error("Could not send right now — please WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="enquiry-section" className="relative bg-ink px-6 py-28 md:px-10 md:py-36">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-14 lg:grid-cols-2">
          <div>
            <ChapterHeader
              index="07"
              title="Start Your Journey"
              testid="enquiry-header"
              heading={
                <>
                  Tell us where. <span className="italic text-sunset">We handle how.</span>
                </>
              }
              blurb="Drop your details and a real travel planner — not a bot — will call or WhatsApp you back with a tailored plan and an honest price."
            />
            <motion.a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              data-testid="enquiry-whatsapp-direct-btn"
              className="inline-flex items-center gap-3 rounded-full border border-teal/40 bg-teal/10 px-7 py-4 text-sm font-medium text-teal transition-colors duration-300 hover:bg-teal hover:text-ink"
            >
              <MessageCircle className="h-4 w-4" />
              Chat now — {WHATSAPP_DISPLAY}
            </motion.a>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              data-testid="enquiry-contact-numbers"
              className="mt-8 flex flex-col gap-4"
            >
              {CONTACTS.map((c) => (
                <a
                  key={c.tel}
                  href={`tel:${c.tel}`}
                  data-testid={`enquiry-phone-${c.tel}`}
                  className="group flex w-fit items-center gap-4 rounded-2xl border border-white/10 bg-surface/60 px-6 py-4 backdrop-blur-xl transition-colors duration-300 hover:border-sunset/40"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-sunset/10">
                    <Phone className="h-4 w-4 text-sunset" />
                  </span>
                  <span>
                    <span className="block text-[11px] uppercase tracking-[0.25em] text-white/40">{c.label}</span>
                    <span className="text-base font-medium text-white transition-colors duration-300 group-hover:text-sunset">{c.phone}</span>
                  </span>
                </a>
              ))}
            </motion.div>
          </div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            data-testid="enquiry-form"
            className="rounded-3xl border border-white/10 bg-surface/80 p-8 backdrop-blur-xl md:p-10"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <input
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                data-testid="enquiry-name-input"
                className={INPUT_CLS}
              />
              <input
                required
                value={form.phone}
                onChange={set("phone")}
                placeholder="Phone / WhatsApp"
                data-testid="enquiry-phone-input"
                className={INPUT_CLS}
              />
            </div>
            <input
              value={form.destination}
              onChange={set("destination")}
              placeholder="Where to? Dubai, Kashmir, Bali…"
              data-testid="enquiry-destination-input"
              className={`${INPUT_CLS} mt-5`}
            />
            <textarea
              value={form.message}
              onChange={set("message")}
              placeholder="Dates, travellers, budget — anything that helps us plan"
              rows={4}
              data-testid="enquiry-message-input"
              className={`${INPUT_CLS} mt-5 resize-none`}
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit-button"
              className="mt-7 inline-flex w-full items-center justify-center gap-3 rounded-full bg-sunset py-4 text-sm font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-coral disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Sending…" : "Send Enquiry"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
